import React, { Suspense, lazy } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../utils/theme';
import { device } from '../utils/mediaQueries';
import { FaGithub, FaLinkedin, FaEnvelope } from 'react-icons/fa';

// Lazy load the 3D scene so it doesn't block initial render
const Scene = lazy(() => import('./Scene'));

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.background};
  padding: 80px 20px 40px;
  position: relative;
  overflow: hidden;
  width: 100%;

  ${device.tablet} {
    padding: 100px 40px 60px;
  }
`;

const Background3D = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.4;
`;

const Content = styled.div`
  text-align: center;
  color: ${theme.text};
  max-width: 800px;
  width: 100%;
  z-index: 1;
  position: relative;
  background: rgba(5, 5, 5, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 2rem 1.25rem;
  border-radius: 20px;
  border: ${theme.glassBorder};

  ${device.mobile} {
    padding: 2.5rem 1.5rem;
  }

  ${device.tablet} {
    padding: 3.5rem 3rem;
    border-radius: 24px;
  }
`;

const Greeting = styled(motion.p)`
  color: ${theme.secondary};
  font-family: ${theme.fontMono};
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  
  ${device.tablet} {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }
`;

const Name = styled(motion.h1)`
  font-size: clamp(2rem, 8vw, 5rem);
  margin: 0 0 0.75rem 0;
  color: ${theme.white};
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.03em;
`;

const Tagline = styled(motion.h2)`
  font-size: clamp(1rem, 4vw, 2rem);
  color: ${theme.secondary};
  margin-bottom: 1.5rem;
  font-weight: 400;
  line-height: 1.4;

  ${device.tablet} {
    margin-bottom: 2rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 0.95rem;
  color: ${theme.text};
  max-width: 550px;
  margin: 0 auto 2rem;
  line-height: 1.7;

  ${device.tablet} {
    font-size: 1.05rem;
    margin-bottom: 2.5rem;
  }
`;

const CTAGroup = styled(motion.div)`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 2rem;

  ${device.tablet} {
    gap: 1rem;
    margin-bottom: 2.5rem;
  }
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0.75rem 1.5rem;
  background: ${theme.white};
  color: ${theme.background};
  text-decoration: none;
  border-radius: 50px;
  font-family: ${theme.fontMono};
  font-size: 0.85rem;
  font-weight: 500;
  transition: ${theme.transition};
  border: 1px solid ${theme.white};
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  ${device.tablet} {
    padding: 0.9rem 2rem;
    font-size: 0.95rem;
  }

  &:hover {
    background: transparent;
    color: ${theme.white};
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(255, 255, 255, 0.15);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: ${theme.white};
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50px;
  font-family: ${theme.fontMono};
  font-size: 0.85rem;
  font-weight: 500;
  transition: ${theme.transition};
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  ${device.tablet} {
    padding: 0.9rem 2rem;
    font-size: 0.95rem;
  }

  &:hover {
    border-color: ${theme.white};
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 1.5rem;

  a {
    color: ${theme.secondary};
    font-size: 1.5rem;
    transition: ${theme.transition};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;

    &:hover {
      color: ${theme.white};
      transform: translateY(-2px);
    }
  }

  ${device.tablet} {
    gap: 2rem;

    a {
      font-size: 1.6rem;
    }
  }
`;

const Hero = () => {
  return (
    <HeroSection id="home">
      <Background3D>
        <Suspense fallback={null}>
          <Scene />
        </Suspense>
      </Background3D>
      
      <Content>
        <Greeting
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Hi there, I'm
        </Greeting>
        <Name
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Gokulakannan
        </Name>
        <Tagline
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          Full-Stack AI Developer
        </Tagline>
        <Description
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          I develop robust web applications using React, Node.js, and modern AI integrations, focusing on performance,
          accessibility, and delivering great user experiences through clean, maintainable code.
        </Description>
        <CTAGroup
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <PrimaryButton href="#contact">
            Get In Touch
          </PrimaryButton>
          <SecondaryButton
            href="/files/resume.pdf"
            download="Gokul_CV.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Resume
          </SecondaryButton>
        </CTAGroup>
        <SocialLinks
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <a href="https://github.com/gokulakannan69" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
            <FaGithub />
          </a>
          <a href="https://www.linkedin.com/in/gokula-kannan-2822a22b5/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <FaLinkedin />
          </a>
          <a href="mailto:gokulkannangk28@gmail.com" aria-label="Email">
            <FaEnvelope />
          </a>
        </SocialLinks>
      </Content>
    </HeroSection>
  );
};

export default Hero;